import * as React from "react"
import { Select as SelectPrimitive } from "@base-ui/react/select"
import { cva, type VariantProps } from "class-variance-authority"
import { CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react"

import {
  formControlDataReadOnly,
  formControlFieldBase,
  formControlPlaceholderSlot,
  formControlShellHover,
  formControlVariantMap,
} from "@/lib/form-control-variants"
import { cn } from "@/lib/utils"

const SelectReadOnlyContext = React.createContext(false)

// Base UI's <Select.Value> renders the raw value unless the root <Select.Root>
// receives an `items` prop mapping values → labels. To keep the call sites
// ergonomic (`<SelectItem value="apple">Apple</SelectItem>`) we walk the JSX
// children once and synthesize that mapping ourselves.
type DerivedSelectItem = { value: unknown; label: React.ReactNode }

function collectSelectItems(
  node: React.ReactNode,
  acc: DerivedSelectItem[]
): DerivedSelectItem[] {
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) return

    if (child.type === SelectItem) {
      const itemProps = child.props as {
        value?: unknown
        children?: React.ReactNode
      }
      acc.push({ value: itemProps.value, label: itemProps.children })
      return
    }

    const childProps = child.props as { children?: React.ReactNode } | null
    if (childProps?.children != null) {
      collectSelectItems(childProps.children, acc)
    }
  })
  return acc
}

function Select<Value, Multiple extends boolean | undefined = false>({
  readOnly = false,
  onOpenChange,
  ...props
}: SelectPrimitive.Root.Props<Value, Multiple> & { readOnly?: boolean }) {
  const { items, children } = props
  const resolvedItems = React.useMemo(
    () => items ?? collectSelectItems(children, []),
    [items, children]
  )

  function handleOpenChange(
    open: boolean,
    eventDetails: Parameters<
      NonNullable<SelectPrimitive.Root.Props<Value, Multiple>["onOpenChange"]>
    >[1]
  ) {
    if (readOnly && open) {
      eventDetails.cancel()
      return
    }
    onOpenChange?.(open, eventDetails)
  }

  return (
    <SelectReadOnlyContext.Provider value={readOnly}>
      <SelectPrimitive.Root
        {...props}
        items={resolvedItems}
        onOpenChange={handleOpenChange}
      />
    </SelectReadOnlyContext.Provider>
  )
}

function SelectGroup({ className, ...props }: SelectPrimitive.Group.Props) {
  return (
    <SelectPrimitive.Group
      data-slot="select-group"
      className={cn("scroll-my-1 p-1", className)}
      {...props}
    />
  )
}

function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className={cn("flex flex-1 text-left", className)}
      {...props}
    />
  )
}

const selectTriggerVariants = cva(
  cn(
    "flex w-fit cursor-pointer items-center justify-between gap-2 whitespace-nowrap",
    formControlFieldBase,
    formControlDataReadOnly,
    formControlShellHover,
    "data-popup-open:border-primary",
    formControlPlaceholderSlot,
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    "*:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5"
  ),
  {
    variants: {
      size: {
        xs: "h-6 pr-4 pl-2 text-xs",
        sm: "h-8 pr-4 pl-3 text-sm",
        md: "h-10 px-4 text-base",
        lg: "h-12 pr-4 pl-6 text-lg",
        xl: "h-14 pr-4 pl-6 text-lg",
      },
      variant: formControlVariantMap,
    },
    defaultVariants: {
      size: "md",
      variant: "default",
    },
  }
)

function SelectTrigger({
  className,
  size,
  variant,
  children,
  ...props
}: SelectPrimitive.Trigger.Props & VariantProps<typeof selectTriggerVariants>) {
  const readOnly = React.useContext(SelectReadOnlyContext)

  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-readonly={readOnly ? "" : undefined}
      aria-readonly={readOnly || undefined}
      className={cn(selectTriggerVariants({ size, variant }), className)}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon
        render={
          <CaretDownIcon className="pointer-events-none size-4 shrink-0 text-muted-foreground" />
        }
      />
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  side = "bottom",
  sideOffset = 4,
  align = "start",
  alignOffset = 0,
  alignItemWithTrigger = true,
  container,
  ...props
}: SelectPrimitive.Popup.Props &
  Pick<
    SelectPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset" | "alignItemWithTrigger"
  > &
  Pick<SelectPrimitive.Portal.Props, "container">) {
  return (
    <SelectPrimitive.Portal container={container}>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        className="isolate z-50"
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          data-align-trigger={alignItemWithTrigger}
          className={cn(
            "relative isolate z-50",
            "max-h-available-height w-anchor-width min-w-36",
            "origin-popup overflow-x-hidden overflow-y-auto",
            "rounded bg-popover text-popover-foreground",
            "shadow-md ring-1 ring-foreground/10",
            "duration-100",
            // When aligned to trigger, suppress the zoom animation
            "data-[align-trigger=true]:animate-none",
            // Slide-in direction by side
            "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
            "data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2",
            "data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2",
            "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95",
            "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            className
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List>{children}</SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({
  className,
  ...props
}: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      className={cn(
        "px-3 py-1.5 text-xs font-semibold text-muted-foreground",
        className
      )}
      {...props}
    />
  )
}

function SelectItem({
  className,
  children,
  ...props
}: SelectPrimitive.Item.Props) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "relative flex w-full cursor-pointer items-center gap-2 select-none",
        "rounded px-3 py-1.5 text-sm text-foreground outline-hidden",
        "transition-colors focus:outline-none",
        // Hover / keyboard-focus pale fill — only on unselected rows so the
        // selected row's primary fill is never overridden when Base UI
        // auto-focuses it on open.
        "not-data-selected:hover:bg-dropdown-item-hover",
        "not-data-selected:focus:bg-dropdown-item-hover",
        // Selected — solid primary background
        "data-selected:bg-primary data-selected:text-primary-foreground",
        // Disabled
        "data-disabled:pointer-events-none data-disabled:opacity-50",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      // Base UI sets `data-selected=""` for boolean-true state, but the
      // shadcn Tailwind preset's `data-selected:` variant only matches
      // `[data-selected="true"]` (Radix's convention, also used by Menu /
      // Menubar in this codebase). Re-emit the attribute as `"true"` via
      // the render callback so the Tailwind variants fire.
      render={(elementProps, state) => (
        <div
          {...elementProps}
          data-selected={state.selected ? "true" : undefined}
        />
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className="flex flex-1 shrink-0 gap-2 whitespace-nowrap">
        {children}
      </SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({
  className,
  ...props
}: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("pointer-events-none -mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  )
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn(
        "sticky top-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 text-muted-foreground",
        "[&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <CaretUpIcon />
    </SelectPrimitive.ScrollUpArrow>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn(
        "sticky bottom-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 text-muted-foreground",
        "[&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <CaretDownIcon />
    </SelectPrimitive.ScrollDownArrow>
  )
}

/**
 * Helper text displayed below a Select trigger.
 * Mirrors the Figma "Label / Bot" pattern — plain foreground text at text-xs.
 * Use the `variant` prop to tint the text to match a validation state.
 */
function SelectHelperText({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"p"> & {
  variant?: "default" | "success" | "warning" | "error"
}) {
  return (
    <p
      data-slot="select-helper-text"
      className={cn(
        "px-1 py-2 text-xs",
        variant === "default" && "text-foreground",
        variant === "success" && "text-success",
        variant === "warning" && "text-warning",
        variant === "error" && "text-destructive",
        className
      )}
      {...props}
    />
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectHelperText,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
// eslint-disable-next-line react-refresh/only-export-components
export { selectTriggerVariants }
